import React, { useState, useEffect } from 'react';

function MediaCarousel({ images = [], video = null, thumbnailImage = null }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Combine all media items.
  // Only include a video slide when there's an actual playable URL — otherwise an empty
  // video object would render a spurious "Video unavailable" placeholder.
  const mediaItems = [
    ...images.map(img => ({ ...img, type: 'image' })),
    ...(video && video.url ? [{ ...video, type: 'video' }] : [])
  ];

  // Reset index when media changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [images, video]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  const goToIndex = (index) => {
    setCurrentIndex(index);
  };

  if (mediaItems.length === 0) {
    return (
      <div style={{
        width: '100%',
        height: '300px',
        backgroundColor: 'var(--surface-panel)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '12px',
        color: 'var(--text-soft)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '0.75rem', opacity: 0.6 }}>
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
          </svg>
          <div>No media available</div>
        </div>
      </div>
    );
  }

  const currentItem = mediaItems[currentIndex];

  // Helper function to check if URL is a Google Drive link
  const isGoogleDriveLink = (url) => {
    if (!url) return false;
    return url.includes('drive.google.com') || url.includes('docs.google.com');
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Main Media Display */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '400px',
        backgroundColor: '#000',
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        {currentItem.type === 'image' ? (
          <img
            src={currentItem.url}
            alt={`Media ${currentIndex + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              cursor: 'pointer'
            }}
            onClick={() => setIsFullscreen(true)}
          />
        ) : (
          <div style={{ width: '100%', height: '100%' }}>
            {currentItem.type === 'video' && currentItem.url && currentItem.publicId ? (
              <video
                controls
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              >
                <source src={currentItem.url} />
                Your browser does not support video playback.
              </video>
            ) : currentItem.type === 'video' && isGoogleDriveLink(currentItem.url) ? (
              // Only show Google Drive button for actual Google Drive links
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--surface-panel)',
                color: 'var(--text-soft)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '1rem' }}>Google Drive Video</div>
                  <a
                    href={currentItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      backgroundColor: 'var(--maroon)',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontWeight: '600'
                    }}
                  >
                    Watch on Google Drive
                  </a>
                </div>
              </div>
            ) : (
              // Fallback for other video types or invalid URLs
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--surface-panel)',
                color: 'var(--text-soft)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div>Video unavailable</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation Arrows */}
        {mediaItems.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '1.2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.7)'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.5)'}
            >
              ‹
            </button>

            <button
              onClick={goToNext}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '1.2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.7)'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.5)'}
            >
              ›
            </button>
          </>
        )}

        {/* Media Type Indicator */}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '0.25rem 0.5rem',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: '600'
        }}>
          {currentIndex + 1}/{mediaItems.length}
        </div>
      </div>

      {/* Thumbnail Navigation */}
      {mediaItems.length > 1 && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginTop: '1rem',
          padding: '0.5rem',
          overflowX: 'auto',
          borderRadius: '8px',
          backgroundColor: 'var(--surface-panel)'
        }}>
          {mediaItems.map((item, index) => (
            <div
              key={index}
              onClick={() => goToIndex(index)}
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: index === currentIndex ? '3px solid var(--maroon)' : '2px solid transparent',
                flexShrink: 0,
                position: 'relative'
              }}
            >
              {item.type === 'image' ? (
                <img
                  src={item.url}
                  alt={`Thumbnail ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#202020',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="6 4 20 12 6 20 6 4" />
                  </svg>
                </div>
              )}
              
              {/* Thumbnail indicator */}
              {item.type === 'image' && item.url === thumbnailImage && (
                <div style={{
                  position: 'absolute',
                  bottom: '2px',
                  left: '2px',
                  right: '2px',
                  backgroundColor: 'var(--maroon)',
                  color: 'white',
                  fontSize: '0.6rem',
                  textAlign: 'center',
                  borderRadius: '2px',
                  fontWeight: '600'
                }}>
                  THUMB
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Indicator Dots */}
      {mediaItems.length > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginTop: '1rem'
        }}>
          {mediaItems.map((_, index) => (
            <button
              key={index}
              onClick={() => goToIndex(index)}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: index === currentIndex ? 'var(--maroon)' : '#e9ecef',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
            />
          ))}
        </div>
      )}

      {/* Fullscreen Modal */}
      {isFullscreen && currentItem.type === 'image' && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer'
          }}
          onClick={() => setIsFullscreen(false)}
        >
          <img
            src={currentItem.url}
            alt="Fullscreen view"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain'
            }}
          />
          
          <button
            onClick={() => setIsFullscreen(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              cursor: 'pointer',
              fontSize: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

export default MediaCarousel; 